import React, { useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
} from "@mui/material";
import Typography from "@mui/material/Typography";

import { JobOperation, Profile } from "../../services/api";

type JobsReTranscodeFormProps = {
  jobTranscodeOperation: JobOperation;
  setJobTranscodeOperation: React.Dispatch<React.SetStateAction<JobOperation>>;
  onTranscode: () => void;
  onCancel: () => void;
  availableProfiles: Profile[];
};

const JobsReTranscodeForm: React.FC<JobsReTranscodeFormProps> = ({
  jobTranscodeOperation,
  setJobTranscodeOperation,
  onTranscode,
  onCancel,
  availableProfiles,
}) => {
  // TODO: if user refresh page on this component, the config context will be not properly assigned.
  const [showJobs, setShowJobs] = useState(false);

  const handleRemoveID = (idToRemove: string) => {
    setJobTranscodeOperation((prevJobTranscodeOperation) => {
      return new JobOperation({
        ...prevJobTranscodeOperation,
        ids: jobTranscodeOperation.ids.filter((id) => id !== idToRemove),
      });
    });
  };

  const toggleProfile = (profileToToggle: Profile) => {
    let profiles = jobTranscodeOperation.profiles;
    if (jobTranscodeOperation.profiles.includes(profileToToggle)) {
      profiles = profiles.filter((profile) => profile !== profileToToggle);
    } else {
      profiles.push(profileToToggle);
    }
    setJobTranscodeOperation((prevJobTranscodeOperation) => {
      return new JobOperation({
        ...prevJobTranscodeOperation,
        profiles: profiles,
      });
    });
  };

  return (
    <Grid container spacing={2} sx={{ minHeight: 400, paddingTop: "30px" }}>
      <Grid item md={6}>
        <Typography variant="h6">Batch Re-Transcode</Typography>
      </Grid>
      <Grid item md={5}>
        <FormGroup row>
          <FormControlLabel
            value="top"
            control={
              <Switch
                onChange={() => {
                  setShowJobs(!showJobs);
                }}
                checked={showJobs}
                data-testid={"toggle-show-jobs"}
                sx={{ marginLeft: "-30px" }}
              />
            }
            label={"Show Jobs"}
            sx={{ marginLeft: "0px", marginBottom: "0px", marginTop: "40px" }}
            labelPlacement="top"
          />
        </FormGroup>
        <FormGroup row>
          {showJobs && jobTranscodeOperation.ids.length > 0 && (
            <Paper sx={{ minWidth: 400, maxWidth: 450 }}>
              <List dense>
                {jobTranscodeOperation.ids.map((id, i) => (
                  <ListItem
                    key={id}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveID(id)}
                        disabled={jobTranscodeOperation.ids.length === 1}
                        data-testid={"remove-id-" + i}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText data-testid={"job-id-" + id} primary={id} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </FormGroup>
        <FormGroup row>
          <FormControlLabel
            value="top"
            control={
              <Switch
                onChange={() => {
                  setJobTranscodeOperation(
                    new JobOperation({
                      ...jobTranscodeOperation,
                      useDefaultProfiles: !jobTranscodeOperation.useDefaultProfiles,
                    }),
                  );
                }}
                checked={jobTranscodeOperation.useDefaultProfiles}
                data-testid={"toggle-default-profiles"}
                sx={{ marginLeft: "-95px" }}
              />
            }
            label="Use Default Profiles:"
            sx={{ marginLeft: "0px", marginBottom: "0px", marginTop: "20px" }}
            labelPlacement="top"
          />
        </FormGroup>
        <FormGroup row>
          {!jobTranscodeOperation.useDefaultProfiles &&
            availableProfiles.map((profile) => (
              <FormControlLabel
                key={profile.profile}
                control={
                  <Checkbox
                    onChange={() => toggleProfile(profile)}
                    checked={jobTranscodeOperation.profiles.includes(profile)}
                    data-testid={"toggle-profile-" + profile.profile}
                  />
                }
                label={profile.profile}
              />
            ))}
        </FormGroup>
        <FormGroup row>
          <FormControlLabel
            value="top"
            control={
              <Switch
                onChange={() => {
                  setJobTranscodeOperation(
                    new JobOperation({
                      ...jobTranscodeOperation,
                      overwrite: !jobTranscodeOperation.overwrite,
                    }),
                  );
                }}
                checked={jobTranscodeOperation.overwrite}
                color="primary"
                sx={{ marginLeft: "-150px" }}
              />
            }
            label="Overwrite Transcode Profile:"
            sx={{ marginLeft: "0px", marginBottom: "20px", marginTop: "20px" }}
            labelPlacement="top"
          />
        </FormGroup>
        <Button
          onClick={onCancel}
          variant="contained"
          sx={{ marginRight: "20px", color: "black", backgroundColor: "lightgrey" }}
        >
          Cancel
        </Button>
        <Button onClick={onTranscode} disabled={!jobTranscodeOperation.validProfiles()} variant="contained">
          Transcode
        </Button>
      </Grid>
    </Grid>
  );
};

export default JobsReTranscodeForm;
